/**
 * Speaker color assignment composable.
 * Uses CSS variables from colors.scss, picking only variants where white text has sufficient contrast.
 * Colors are interleaved across hue families to prevent similar tones from being assigned consecutively.
 *
 * The color map is global (module-scoped) so that every component instance shares
 * the same speaker→color assignments and no color is picked twice for different speakers.
 */
// Color families with white-safe variants (text-on = #fff), ordered lightest→darkest
// for maximum visual spacing when same family is reused
const colorFamilies = [
    ["red-lighten-1", "red-darken-2", "red-darken-4"],
    ["pink-lighten-1", "pink-darken-1", "pink-darken-4"],
    ["purple-lighten-2", "purple", "purple-darken-4"],
    ["deep-purple-lighten-2", "deep-purple", "deep-purple-darken-4"],
    ["indigo-lighten-2", "indigo", "indigo-darken-4"],
    ["blue-lighten-1", "blue-darken-2", "blue-darken-4"],
    ["light-blue", "light-blue-darken-2", "light-blue-darken-4"],
    ["teal-lighten-1", "teal-darken-1", "teal-darken-4"],
    ["green-lighten-1", "green-darken-2", "green-darken-4"],
    ["deep-orange-lighten-1", "deep-orange-darken-2", "deep-orange-darken-4"],
    ["brown-lighten-1", "brown-darken-1", "brown-darken-4"],
    ["blue-grey-lighten-1", "blue-grey-darken-1", "blue-grey-darken-4"]
];
// Family order is fixed, not shuffled, so a speaker keeps the same colour across page loads.
const maxVariants = Math.max(...colorFamilies.map((f) => f.length));
const colorPool = [];
for (let round = 0; round < maxVariants; round++) {
    const roundColors = colorFamilies.filter((f) => round < f.length).map((f) => f[round]);
    colorPool.push(...roundColors.map((c) => `var(--color-${c})`));
}
// Global speaker→color map shared across all composable instances
const speakerColorMap = new Map();
// How far each recording's own walk has gone; one shared counter let whoever rendered first shift the rest.
const nextSlot = new Map();
// Stable pool slot for a string, so the same input lands on the same colour on every page load.
const poolSlot = (value) => {
    let hash = 0;
    for (let index = 0; index < value.length; index++) {
        hash = (hash * 31 + value.charCodeAt(index)) % colorPool.length;
    }
    return hash;
};
// Never the sequential walk: it restarts per page and shifts with whoever spoke first, so it can't be theirs.
export const getParticipantColor = (userId) => colorPool[poolSlot(String(userId))];
// `source` scopes both the key and the walk, so a label shared across recordings isn't one colour.
export const getSpeakerColor = (speaker, source) => {
    if (!speaker)
        return colorPool[0];
    const hasSource = source !== undefined && source !== null && source !== "";
    const scope = hasSource ? String(source) : "";
    const key = hasSource ? `${scope}:${speaker}` : speaker;
    if (!speakerColorMap.has(key)) {
        const slot = nextSlot.get(scope) ?? (hasSource ? poolSlot(scope) : 0);
        nextSlot.set(scope, slot + 1);
        speakerColorMap.set(key, colorPool[slot % colorPool.length]);
    }
    return speakerColorMap.get(key);
};
